import React, { useState, useCallback } from 'react';
import { Keyword } from './KeywordTable';
import { TextField, Button } from '@mui/material';
import KeywordCounter from '../apps/KeywordCounter';

interface KeywordListProps {
    keywords: Keyword[];
    excludedKeywords: Keyword[];
    isExcluded: boolean;
    parsedPlainText: string | null;
    mergeStatus: string;
    onUpdateKeyword: (id: number, newKeywordValue: string, isExcluded: boolean) => void;
    onDeleteKeyword: (id: number, isExcluded: boolean) => void;
    onStartEditingKeyword: (id: number, isExcluded: boolean) => void;
    onFinishEditingKeyword: (id: number, newKeyword: string, isExcluded: boolean) => void;
    handleKeywordCountUpdate: (keyword: string, count: number) => void;
}

const KeywordList: React.FC<KeywordListProps> = ({
    keywords,
    excludedKeywords,
    isExcluded,
    parsedPlainText,
    mergeStatus,
    onUpdateKeyword,
    onDeleteKeyword,
    onStartEditingKeyword,
    onFinishEditingKeyword,
    handleKeywordCountUpdate
}) => {
    const [isComposing, setIsComposing] = useState(false);

    const handleCompositionStart = useCallback(() => {
        setIsComposing(true);
    }, []);

    const handleCompositionEnd = useCallback(() => {
        setIsComposing(false);
    }, []);

    const handleUpdateKeyword = useCallback((id: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, isExcluded: boolean) => {
        onUpdateKeyword(id, e.target.value, isExcluded);
    }, [onUpdateKeyword]);

    const handleFinishEditingKeyword = useCallback((id: number, newKeyword: string, isExcluded: boolean) => {
        onFinishEditingKeyword(id, newKeyword, isExcluded);
    }, [onFinishEditingKeyword]);

    const handleKeyDown = useCallback((id: number, e: React.KeyboardEvent<HTMLInputElement>, isExcluded: boolean) => {
        if (e.key === 'Enter' && !isComposing && !e.nativeEvent.isComposing) {
            const newKeyword = (e.target as HTMLInputElement).value;
            handleFinishEditingKeyword(id, newKeyword, isExcluded);
            e.preventDefault();
        }
    }, [handleFinishEditingKeyword, isComposing]);

    return (
        <table className="keyword-table">
            <thead>
                <tr>
                    <th className='count-keyword-col'>キーワード</th>
                    <th className='count-result-col'>ヒット数</th>
                    <th className='action-col'>削除</th>
                </tr>
            </thead>
            <tbody>
                {keywords.map((keyword: Keyword) => (
                    <tr key={keyword.id}>
                        <td onClick={() => onStartEditingKeyword(keyword.id, isExcluded)} style={{ cursor: 'pointer' }}>
                            {keyword.isEditing ? (
                                <TextField
                                    type="text"
                                    value={keyword.keyword}
                                    onChange={(e) => handleUpdateKeyword(keyword.id, e, keyword.isExcluded ?? isExcluded)}
                                    onKeyDown={(e) => handleKeyDown(keyword.id, e as React.KeyboardEvent<HTMLInputElement>, keyword.isExcluded ?? isExcluded)}
                                    onCompositionStart={handleCompositionStart}
                                    onCompositionEnd={handleCompositionEnd}
                                    onBlur={(e) => handleFinishEditingKeyword(keyword.id, e.target.value, keyword.isExcluded ?? isExcluded)}
                                    autoFocus
                                />
                            ) : (
                                <span>{keyword.keyword}</span>
                            )}
                        </td>
                        <td>
                            <KeywordCounter
                                parsedPlainText={parsedPlainText}
                                keyword={keyword.keyword}
                                excludedKeywords={excludedKeywords.map(k => k.keyword)}
                                updateCount={handleKeywordCountUpdate}
                                mergeStatus={mergeStatus}
                                isExcluded={keyword.isExcluded ?? isExcluded}
                            />
                        </td>
                        <td className='delete-col'>
                            <Button onClick={() => onDeleteKeyword(keyword.id, isExcluded)} variant='outlined'>削除</Button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default KeywordList;
